import { MenuOption } from 'ComponentLibrary/src/atoms/Options/OptionsMenu'
import { Box, Stack, StackProps, styled, SvgIcon, useTheme } from '@mui/material'
import { OptionsMenu } from 'ComponentLibrary/src/atoms/Options'
import { TextWithEllipsis } from 'ComponentLibrary/src/molecules/TextWithEllipsis'
import { ComponentProps, ElementType } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'

type BaseListItemProps<T extends ElementType = 'li'> = {
    title: string
    subtitle?: React.ReactNode
    icon?: React.ReactNode
    options?: MenuOption[]
    optionsLabel?: string
    optionsMenuProps?: Omit<ComponentProps<typeof OptionsMenu>, 'options' | 'ariaLabel'>
    className?: string
    size?: 'medium' | 'large'
} & StackProps<T, { component?: T }>

export function BaseListItem<TComponent extends ElementType>({
    title,
    subtitle,
    options,
    icon,
    className,
    size = 'large',
    optionsLabel,
    optionsMenuProps,
    ...props
}: BaseListItemProps<TComponent>) {
    return (
        <Stack direction="row" width="100%" className={className} alignItems="center" aria-label={props['aria-label'] ?? title} {...props}>
            {icon && <IconWrapper $size={size}>{icon}</IconWrapper>}
            <Stack direction="column" minWidth={0} paddingRight={tokens.SizingSpacing075}>
                <TextWithEllipsis
                    typographyProps={{
                        variant: size === 'medium' ? 'bodyTextMedium' : 'bodyTextLarge',
                        className: 'listItemTitle',
                    }}
                    tooltipText={title}
                >
                    {title}
                </TextWithEllipsis>

                {typeof subtitle === 'string' ? (
                    <TextWithEllipsis typographyProps={{ variant: 'bodyTextSmall', color: tokens.ColorTextDefault }} tooltipText={subtitle}>
                        {subtitle}
                    </TextWithEllipsis>
                ) : (
                    subtitle
                )}
            </Stack>
            {options && (
                <MenuContainer>
                    <OptionsMenu {...optionsMenuProps} options={options} tooltip="Optionen" ariaLabel={optionsLabel} />
                </MenuContainer>
            )}
        </Stack>
    )
}

const IconWrapper = styled(SvgIcon, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $size: 'large' | 'medium' }>(({ theme, $size }) => ({
    margin: $size === 'medium' ? '8px' : `0 16px 0 0`,
    color: theme.palette.primary.main,
    fontSize: $size === 'large' ? '24px' : '20px',
}))

const MenuContainer = styled(Box)(({ theme }) => ({
    marginLeft: 'auto',
    color: theme.palette.customtext.darkblue100,
}))

export function ClickableListItem({ onClick, ...props }: Omit<BaseListItemProps<'button'>, 'component'>) {
    const ClickableListItemStyle = {
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        padding: 0,
        '&:hover': {
            '& .listItemTitle': { textDecoration: 'underline' },
        },
    }

    return <BaseListItem {...props} component="button" onClick={onClick} sx={ClickableListItemStyle} />
}

export function SelectableListItem({
    onClick,
    selected,
    ...props
}: Omit<BaseListItemProps<'button'>, 'component'> & { selected?: boolean }) {
    const theme = useTheme()

    const SelectableListItemStyle = {
        padding: '10px 14px 12px 14px',
        cursor: 'pointer',
        borderRadius: '4px',
        border: 0,
        background: 'none',
        '&:hover': {
            backgroundColor: theme.palette.secondary1.lightblue,
        },
        '&:active,&:hover:active': {
            boxShadow: `0 0 0 1.5px ${theme.palette.primary2.green100}`,
            backgroundColor: theme.palette.primary2.green20,
        },
    }

    const SelectedStyling = {
        '&:not(:active)': {
            boxShadow: `0 0 0 1px ${theme.palette.primary2.green100}`,
            backgroundColor: theme.palette.primary2.green10,
        },
    }

    const NonSelectedStyling = {
        '&:focus': {
            backgroundColor: theme.palette.secondary1.lightblue,
            boxShadow: `0 0 0 2px ${theme.palette.primary1.blue100}, 0 0 0 2px ${theme.palette.white}`,
        },
    }

    return (
        <BaseListItem
            {...props}
            onClick={onClick}
            component="button"
            aria-selected={selected}
            sx={selected ? { ...SelectableListItemStyle, ...SelectedStyling } : { ...SelectableListItemStyle, ...NonSelectedStyling }}
        />
    )
}
